import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  getEmployeeDashboard,
  getEmployeeProfile,
} from '../../../shared/api/employee.service';
import { useEmployeeSession } from '../../../shared/hooks/use-employee-session';
import {
  resolveEmployeeProfileName,
  resolveEmployeeSessionCountryCode,
} from '../../../shared/model';
import { formatDashboardCurrency } from '../model';

import { HomeDashboardBalanceCard } from './home-dashboard-balance-card';
import { HomeDashboardHeaderRow } from './home-dashboard-header-row';

const Section = styled.div.attrs({
  'data-ui': 'home-dashboard-top-section',
})`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(19)};
`;

type HomeDashboardTopSectionProps = {
  onOpenProfile?: () => void;
  onOpenReferral?: () => void;
  onOpenWithdrawal?: () => void;
};

function resolveEmployeeDashboardTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    return '';
  }
}

export function HomeDashboardTopSection({
  onOpenProfile,
  onOpenReferral,
  onOpenWithdrawal,
}: HomeDashboardTopSectionProps = {}) {
  const { t } = useTranslation();
  const { authContact, status } = useEmployeeSession();
  const profileQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeProfile,
    queryKey: ['employee', 'profile'],
  });
  const dashboardQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: () => getEmployeeDashboard(resolveEmployeeDashboardTimeZone()),
    queryKey: ['employee', 'dashboard'],
  });
  const profile = profileQuery.data ?? null;
  const wallet = dashboardQuery.data?.wallet ?? null;
  const dashboardProfile = dashboardQuery.data?.profile ?? null;
  const headerProfile = profile ?? dashboardProfile;
  const countryCode = resolveEmployeeSessionCountryCode(
    headerProfile,
    authContact,
  );
  const profileName = resolveEmployeeProfileName(
    headerProfile,
    t('homeDashboard.profileName'),
  );
  const balanceValue = formatDashboardCurrency(wallet?.balance, countryCode);
  const isRefreshing = profileQuery.isFetching || dashboardQuery.isFetching;
  const refreshDisabled = status !== 'authenticated' || isRefreshing;
  const refreshDashboard = () => {
    if (refreshDisabled) {
      return;
    }

    void Promise.all([profileQuery.refetch(), dashboardQuery.refetch()]);
  };

  return (
    <Section>
      <HomeDashboardHeaderRow
        avatarUrl={headerProfile?.avatarUrl ?? null}
        isRefreshing={isRefreshing}
        onOpenProfile={onOpenProfile}
        onOpenReferral={onOpenReferral}
        onRefresh={refreshDashboard}
        profileName={profileName}
        referralBonus={t('homeDashboard.referralBonus')}
        refreshAriaLabel={t('homeDashboard.refreshAria')}
        refreshDisabled={refreshDisabled}
      />
      <HomeDashboardBalanceCard
        brandTagline={t('homeDashboard.brandTagline')}
        balanceLabel={t('homeDashboard.balanceLabel')}
        balanceValue={balanceValue}
        onOpenWithdrawal={onOpenWithdrawal}
        withdrawLabel={t('homeDashboard.withdraw')}
      />
    </Section>
  );
}
